import { useState } from 'react';
import {
  Container,
  Group,
  Title,
  Text,
  Table,
  Badge,
  Button,
  SimpleGrid,
  Card,
  Stack,
  Modal,
  Skeleton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconX, IconEye } from '@tabler/icons-react';
import {
  useOrderIntakeQueue,
  useOrderIntakeStats,
  useApproveIntakeDraft,
  useRejectIntakeDraft,
} from '@/hooks/usePortalApi';

const SOURCE_COLORS: Record<string, string> = {
  sms: 'blue',
  voice: 'green',
  email: 'violet',
  reorder_nudge: 'orange',
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'yellow',
  approved: 'green',
  rejected: 'red',
  processing: 'blue',
};

export default function OrderIntake() {
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedDraft, setSelectedDraft] = useState<any>(null);

  const { data: queue, isLoading: queueLoading } = useOrderIntakeQueue();
  const { data: stats, isLoading: statsLoading } = useOrderIntakeStats();
  const approveMutation = useApproveIntakeDraft();
  const rejectMutation = useRejectIntakeDraft();

  const handleReview = (draft: any) => {
    setSelectedDraft(draft);
    open();
  };

  const handleApprove = async (draftId: number) => {
    try {
      await approveMutation.mutateAsync(draftId);
      notifications.show({
        title: 'Draft Approved',
        message: 'Order has been approved and created successfully',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      close();
    } catch (error) {
      notifications.show({
        title: 'Approval Failed',
        message: 'Failed to approve draft order',
        color: 'red',
        icon: <IconX size={16} />,
      });
    }
  };

  const handleReject = async (draftId: number) => {
    try {
      await rejectMutation.mutateAsync(draftId);
      notifications.show({
        title: 'Draft Rejected',
        message: 'Order draft has been rejected',
        color: 'orange',
        icon: <IconX size={16} />,
      });
      close();
    } catch (error) {
      notifications.show({
        title: 'Rejection Failed',
        message: 'Failed to reject draft order',
        color: 'red',
        icon: <IconX size={16} />,
      });
    }
  };

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="md">
        <Stack gap="xs">
          <Title order={2}>Order Intake</Title>
          <Text size="sm" c="dimmed">
            Review AI-generated draft orders
          </Text>
        </Stack>
      </Group>

      <SimpleGrid cols={4} mb="xl">
        {statsLoading ? (
          <>
            <Skeleton height={100} />
            <Skeleton height={100} />
            <Skeleton height={100} />
            <Skeleton height={100} />
          </>
        ) : (
          <>
            <Card withBorder>
              <Stack gap="xs">
                <Text size="sm" c="dimmed">
                  Total Drafts
                </Text>
                <Text size="xl" fw={700}>
                  {stats?.total_drafts || 0}
                </Text>
              </Stack>
            </Card>
            <Card withBorder>
              <Stack gap="xs">
                <Text size="sm" c="dimmed">
                  Pending Review
                </Text>
                <Text size="xl" fw={700}>
                  {stats?.pending_review || 0}
                </Text>
              </Stack>
            </Card>
            <Card withBorder>
              <Stack gap="xs">
                <Text size="sm" c="dimmed">
                  Approved Today
                </Text>
                <Text size="xl" fw={700}>
                  {stats?.approved_today || 0}
                </Text>
              </Stack>
            </Card>
            <Card withBorder>
              <Stack gap="xs">
                <Text size="sm" c="dimmed">
                  Auto-Detected
                </Text>
                <Text size="xl" fw={700}>
                  {stats?.auto_detected || 0}
                </Text>
              </Stack>
            </Card>
          </>
        )}
      </SimpleGrid>

      {queueLoading ? (
        <Stack gap="xs">
          <Skeleton height={50} />
          <Skeleton height={50} />
          <Skeleton height={50} />
        </Stack>
      ) : !queue || queue.length === 0 ? (
        <Text ta="center" c="dimmed" py="xl">
          No draft orders pending review
        </Text>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Draft #</Table.Th>
              <Table.Th>Source</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th>Items</Table.Th>
              <Table.Th>Est. Total</Table.Th>
              <Table.Th>Confidence %</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {queue.map((draft: any) => (
              <Table.Tr key={draft.id}>
                <Table.Td>#{draft.id}</Table.Td>
                <Table.Td>
                  <Badge color={SOURCE_COLORS[draft.source]} variant="light">
                    {draft.source}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  {new Date(draft.created_at).toLocaleDateString()}
                </Table.Td>
                <Table.Td>{draft.item_count}</Table.Td>
                <Table.Td>${draft.estimated_total?.toFixed(2)}</Table.Td>
                <Table.Td>{draft.confidence_score}%</Table.Td>
                <Table.Td>
                  <Badge color={STATUS_COLORS[draft.status]} variant="light">
                    {draft.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <Button
                      size="xs"
                      variant="subtle"
                      leftSection={<IconEye size={14} />}
                      onClick={() => handleReview(draft)}
                    >
                      Review
                    </Button>
                    <Button
                      size="xs"
                      color="green"
                      onClick={() => handleApprove(draft.id)}
                      loading={approveMutation.isPending}
                    >
                      Approve
                    </Button>
                    <Button
                      size="xs"
                      color="red"
                      variant="light"
                      onClick={() => handleReject(draft.id)}
                      loading={rejectMutation.isPending}
                    >
                      Reject
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Modal opened={opened} onClose={close} title="Draft Order Details" size="md">
        {selectedDraft && (
          <Stack gap="md">
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Source
              </Text>
              <Badge color={SOURCE_COLORS[selectedDraft.source]} variant="light">
                {selectedDraft.source}
              </Badge>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                AI Confidence Score
              </Text>
              <Text fw={600}>{selectedDraft.confidence_score}%</Text>
            </Group>

            <Stack gap="xs">
              <Text size="sm" fw={600}>
                Draft Items
              </Text>
              <Table striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Product</Table.Th>
                    <Table.Th>Qty</Table.Th>
                    <Table.Th>Unit Price</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {selectedDraft.items?.map((item: any, idx: number) => (
                    <Table.Tr key={idx}>
                      <Table.Td>{item.product_name}</Table.Td>
                      <Table.Td>{item.quantity}</Table.Td>
                      <Table.Td>${item.unit_price?.toFixed(2)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Stack>

            <Group justify="flex-end" mt="md">
              <Button
                color="green"
                onClick={() => handleApprove(selectedDraft.id)}
                loading={approveMutation.isPending}
              >
                Approve Order
              </Button>
              <Button
                color="red"
                variant="light"
                onClick={() => handleReject(selectedDraft.id)}
                loading={rejectMutation.isPending}
              >
                Reject
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Container>
  );
}
